import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { validateAmountRuntime } from './Amount.utils';
import { CURRENCY_SYMBOL, INPUT_STYLE } from './AmountInput.content';
import { AmountInputProps } from './AmountInput.types';
import Box from '../Box/Box';
import Text from '../Text/Text';
import Input from '../Input/Input';

function AmountInput({
  value,
  onFocus,
  onBlur,
  onChangeText,
  ...rest
}: AmountInputProps) {
  const [focused, setFocused] = useState(false);
  const animatedValue = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
  }));
  const formattedValue = useMemo(() => value?.replace('.', ','), [value]);

  const handleFocus = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleChangeText = useCallback(
    (value: string) => {
      if (value && !validateAmountRuntime(value)) {
        return;
      }

      onChangeText?.(value.replace(',', '.'));
    },
    [onChangeText],
  );

  useEffect(() => {
    animatedValue.value = withTiming(Number(!!value || focused));
  }, [value, focused, animatedValue]);

  return (
    <Box flexDirection="row" alignItems="center">
      <Input
        {...rest}
        flex={1}
        value={formattedValue}
        style={INPUT_STYLE}
        onFocus={handleFocus}
        onBlur={handleBlur}
        keyboardType="numeric"
        onChangeText={handleChangeText}
      />
      <Text
        variant="body-m-semi"
        position="absolute"
        left="space-300"
        bottom="space-200"
        color="darkSurfacePrimary"
        paddingBottom="space-050"
        paddingLeft="space-050"
        animatedStyle={animatedStyle}
      >
        {CURRENCY_SYMBOL}
      </Text>
    </Box>
  );
}

export default memo(AmountInput);
